<script setup lang="ts">
import { computed, ref } from 'vue';
import { useWidgetSettings } from './composables/useWidgetSettings';
import WidgetTitleSection from './sections/WidgetTitleSection.vue';
import FilterBySection from './sections/FilterBySection.vue';
import GroupOrderSection from './sections/GroupOrderSection.vue';
import WeightsSection from './sections/WeightsSection.vue';
import DateWithinMonthsSection from './sections/DateWithinMonthsSection.vue';
import BenchmarksSection from './sections/BenchmarksSection.vue';
import ResultsSection from './sections/ResultsSection.vue';
import DateRangeSection from './sections/DateRangeSection.vue';
import DisplayTypeSection from './sections/DisplayTypeSection.vue';
import AdditionalFieldsSection from './sections/AdditionalFieldsSection.vue';
import ViewMoreSettings from './sections/ViewMoreSettings.vue';
import ConnxSkeletonWidgetFilters from './ConnxSkeletonWidgetFilters.vue';
import type { DashboardWidget } from '@/interfaces/Dashboard';

const props = defineProps<{ widgetId: string; editing: boolean }>();
const emit = defineEmits<{
  'toggle-edit': [];
}>();

const settingsRef = ref<HTMLElement>();
const helpFieldTextExpanded = ref(false);

const { dashboardStore, widget, parameters, loaded, errors, onFieldError, resetWidget, widgetBeforeEdit, hasErrors } =
  useWidgetSettings(props.widgetId);

defineExpose({
  hasErrors,
  resetWidget: () => resetWidget(widgetBeforeEdit.value!),
});

type WidgetParameters = NonNullable<DashboardWidget['defaultJsonProperties']['parameters']>;
const shouldShowSection = (sectionName: keyof WidgetParameters) =>
  parameters.value?.[sectionName as keyof typeof parameters.value];

// Event handlers for child components
const handleUpdateTitle = (title: string) => {
  if (!widget.value) return;

  dashboardStore.updateDashboardWidget({
    ...widget.value,
    defaultJsonProperties: {
      ...widget.value.defaultJsonProperties,
      title,
    },
  });
};

const handleUpdateSubtitle = (subtitle: string) => {
  if (!widget.value) return;

  dashboardStore.updateDashboardWidget({
    ...widget.value,
    defaultJsonProperties: {
      ...widget.value.defaultJsonProperties,
      subtitle,
    },
  });
};

const handleUpdateHelpFieldText = (helpFieldText: string) => {
  if (!widget.value) return;

  dashboardStore.updateDashboardWidget({
    ...widget.value,
    defaultJsonProperties: {
      ...widget.value.defaultJsonProperties,
      helpFieldText,
    },
  });
};

const handleCloneHelpText = () => {
  if (!widget.value) return;

  dashboardStore.updateDashboardWidget({
    ...widget.value,
    defaultJsonProperties: {
      ...widget.value.defaultJsonProperties,
      helpFieldText: widget.value.helpFieldText,
    },
  });
};

const handleResetHelpText = () => {
  if (!widget.value) return;

  dashboardStore.updateDashboardWidget({
    ...widget.value,
    defaultJsonProperties: {
      ...widget.value.defaultJsonProperties,
      helpFieldText: undefined,
    },
  });
};

const handleUpdateDisplayType = (type: string) => {
  if (!widget.value) return;

  dashboardStore.updateDashboardWidget({
    ...widget.value,
    defaultJsonProperties: {
      ...widget.value.defaultJsonProperties,
      currentDisplayType: type,
      displayedAsTable: type === 'table',
    },
  });
};

const handleUpdateResults = (value: number) => {
  if (!widget.value?.defaultJsonProperties?.parameters) return;

  dashboardStore.updateDashboardWidget({
    ...widget.value,
    defaultJsonProperties: {
      ...widget.value.defaultJsonProperties,
      parameters: {
        ...widget.value.defaultJsonProperties.parameters,
        results: value,
      },
    },
  });
};

const handleUpdateBenchmarks = ({ values }: { values: [number, number] }) => {
  if (!widget.value?.defaultJsonProperties?.parameters?.benchmarks) return;

  dashboardStore.updateDashboardWidget({
    ...widget.value,
    defaultJsonProperties: {
      ...widget.value.defaultJsonProperties,
      parameters: {
        ...widget.value.defaultJsonProperties.parameters,
        benchmarks: {
          ...widget.value.defaultJsonProperties.parameters.benchmarks,
          values,
        },
      },
    },
  });
};

const handleUpdateAdditionalField = ({ key, value }: { key: string; value: any }) => {
  if (!widget.value?.defaultJsonProperties?.parameters?.additionalFields) return;

  const updatedFields = widget.value.defaultJsonProperties.parameters.additionalFields.map((field) =>
    field.key === key ? { ...field, value } : field
  );

  dashboardStore.updateDashboardWidget({
    ...widget.value,
    defaultJsonProperties: {
      ...widget.value.defaultJsonProperties,
      parameters: {
        ...widget.value.defaultJsonProperties.parameters,
        additionalFields: updatedFields,
      },
    },
  });
};

const handleUpdatePreviousRangeSelected = (selected: boolean) => {
  if (!widget.value?.defaultJsonProperties?.parameters?.previousRange) return;

  dashboardStore.updateDashboardWidget({
    ...widget.value,
    defaultJsonProperties: {
      ...widget.value.defaultJsonProperties,
      parameters: {
        ...widget.value.defaultJsonProperties.parameters,
        previousRange: {
          ...widget.value.defaultJsonProperties.parameters.previousRange,
          selected,
        },
      },
    },
  });
};

const handleUpdatePreviousMonths = (value: number | null) => {
  if (!widget.value?.defaultJsonProperties?.parameters?.previousMonths) return;

  dashboardStore.updateDashboardWidget({
    ...widget.value,
    defaultJsonProperties: {
      ...widget.value.defaultJsonProperties,
      parameters: {
        ...widget.value.defaultJsonProperties.parameters,
        previousMonths: {
          ...widget.value.defaultJsonProperties.parameters.previousMonths,
          value: value!,
        },
      },
    },
  });
};

const handleUpdateWithinXMonths = (value: number | null) => {
  if (!widget.value?.defaultJsonProperties?.parameters?.withinXMonths) return;

  dashboardStore.updateDashboardWidget({
    ...widget.value,
    defaultJsonProperties: {
      ...widget.value.defaultJsonProperties,
      parameters: {
        ...widget.value.defaultJsonProperties.parameters,
        withinXMonths: {
          ...widget.value.defaultJsonProperties.parameters.withinXMonths,
          value: value!,
        },
      },
    },
  });
};

const handleUpdatePreviousRangeFrom = (value: string) => {
  if (!widget.value?.defaultJsonProperties?.parameters?.previousRange?.value) return;

  const newValue = [...widget.value.defaultJsonProperties.parameters.previousRange.value] as [string, string];
  newValue[0] = value;

  dashboardStore.updateDashboardWidget({
    ...widget.value,
    defaultJsonProperties: {
      ...widget.value.defaultJsonProperties,
      parameters: {
        ...widget.value.defaultJsonProperties.parameters,
        previousRange: {
          ...widget.value.defaultJsonProperties.parameters.previousRange,
          value: newValue,
        },
      },
    },
  });
};

const handleUpdatePreviousRangeTo = (value: string) => {
  if (!widget.value?.defaultJsonProperties?.parameters?.previousRange?.value) return;

  const newValue = [...widget.value.defaultJsonProperties.parameters.previousRange.value] as [string, string];
  newValue[1] = value;

  dashboardStore.updateDashboardWidget({
    ...widget.value,
    defaultJsonProperties: {
      ...widget.value.defaultJsonProperties,
      parameters: {
        ...widget.value.defaultJsonProperties.parameters,
        previousRange: {
          ...widget.value.defaultJsonProperties.parameters.previousRange,
          value: newValue,
        },
      },
    },
  });
};

const safeResults = computed(() => {
  const resultsValue = parameters.value?.results;
  return resultsValue !== undefined && resultsValue !== null ? resultsValue : -1;
});
</script>

<template>
  <div v-if="loaded && widget" ref="settingsRef">
    <WidgetTitleSection
      v-if="editing"
      :widget="widget"
      :errors="errors"
      @field-error="onFieldError"
      @toggle-help-expanded="(val) => (helpFieldTextExpanded = val)"
      @update:title="handleUpdateTitle"
      @update:subtitle="handleUpdateSubtitle"
      @update:help-field-text="handleUpdateHelpFieldText"
      @clone-help-text="handleCloneHelpText"
      @reset-help-text="handleResetHelpText"
    />

    <FilterBySection
      v-if="shouldShowSection('filterBy')"
      :filters="parameters?.filterBy"
      :errors="errors"
      @field-error="onFieldError"
    />

    <GroupOrderSection
      v-if="shouldShowSection('groupBy') || shouldShowSection('orderBy')"
      :widget="widget"
      :group-by="parameters?.groupBy"
      :order-by="parameters?.orderBy"
      :errors="errors"
      @field-error="onFieldError"
    />

    <WeightsSection
      v-if="shouldShowSection('weights')"
      :weights="parameters?.weights"
      :errors="errors"
      @field-error="onFieldError"
    />

    <DateWithinMonthsSection
      v-if="shouldShowSection('withinXMonths')"
      :previous-months="parameters?.previousMonths"
      :previous-range="parameters?.previousRange"
      :within-x-months="parameters?.withinXMonths"
      :errors="errors"
      @field-error="onFieldError"
      @update:within-x-months="handleUpdateWithinXMonths"
    />

    <BenchmarksSection
      v-if="shouldShowSection('benchmarks')"
      :benchmarks="parameters?.benchmarks"
      :errors="errors"
      @field-error="onFieldError"
      @update:benchmarks="handleUpdateBenchmarks"
    />

    <ResultsSection
      v-if="parameters?.results"
      :results="safeResults"
      :errors="errors"
      @field-error="onFieldError"
      @update:results="handleUpdateResults"
    />

    <DateRangeSection
      v-if="shouldShowSection('previousMonths') || shouldShowSection('previousRange')"
      :previous-months="parameters?.previousMonths"
      :previous-range="parameters?.previousRange"
      :errors="errors"
      @field-error="onFieldError"
      @update:previous-range-selected="handleUpdatePreviousRangeSelected"
      @update:previous-months="handleUpdatePreviousMonths"
      @update:previous-range-from="handleUpdatePreviousRangeFrom"
      @update:previous-range-to="handleUpdatePreviousRangeTo"
    />

    <AdditionalFieldsSection
      v-if="shouldShowSection('additionalFields')"
      :fields="parameters?.additionalFields"
      :errors="errors"
      @field-error="onFieldError"
      @update:field="handleUpdateAdditionalField"
    />

    <DisplayTypeSection
      v-if="editing && widget.defaultJsonProperties?.availableDisplayTypes"
      :widget="widget"
      :available-types="widget.defaultJsonProperties.availableDisplayTypes"
      @update:display-type="handleUpdateDisplayType"
    />

    <ViewMoreSettings v-if="!editing" @view-more="emit('toggle-edit')" />
  </div>

  <ConnxSkeletonWidgetFilters v-else />
</template>
